/**
 * web/src/shell/listeners.ts — the shell's global listeners, moved verbatim
 * out of App.tsx (card WUHnArc6lNID) as hooks App calls in the original
 * declaration order, so effect order and dependency arrays are unchanged:
 * the #settings-<section> deep link (hashchange), destination back/forward
 * (popstate), the settings-hash cleanup, presence + tab visibility, and the
 * header date's midnight/foreground refresh.
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { settingsTabFromHash } from '../screens/Settings';
import type { useSession } from '../hooks/useSession';
import { destFromHash } from './dest';
import { headerDate } from './header';
import type { Tab } from './dest';

type Session = ReturnType<typeof useSession>;
type SetBool = Dispatch<SetStateAction<boolean>>;
type SetView = Dispatch<SetStateAction<'help' | null>>;
type SetStr = Dispatch<SetStateAction<string>>;
type SetTab = Dispatch<SetStateAction<Tab>>;

export function useSettingsDeepLink(
  { setSettingsTab, setShowingAI, setExtraView, setSettingsOpen }: {
    setSettingsTab: SetStr;
    setShowingAI: SetBool;
    setExtraView: SetView;
    setSettingsOpen: SetBool;
  },
) {
  // Deep link (card DI31pLXCFc2C): #settings-<section> opens Settings on
  // that section — a support message can say "Settings → Concierge" and ship
  // the link. Reads the hash on arrival and on hashchange (back/forward).
  useEffect(() => {
    const apply = () => {
      const key = settingsTabFromHash(window.location.hash);
      if (!key) return;
      setSettingsTab(key);
      setShowingAI(false);
      setExtraView(null);
      setSettingsOpen(true);
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, []);
}

export function useDestPopstate(
  { setSettingsOpen, setShowingAI, setExtraView, setUsTab, setLifeTab, setTab }: {
    setSettingsOpen: SetBool;
    setShowingAI: SetBool;
    setExtraView: SetView;
    setUsTab: SetStr;
    setLifeTab: SetStr;
    setTab: SetTab;
  },
) {
  // Back/forward retraces screen changes (card Mn55eOvFgvC0, H4): a
  // destination hash (#us-game, #life-plan, …) pushed by navigateTo is
  // re-applied on popstate, so the browser Back button steps between the
  // screens you actually visited instead of leaving the app. Only acts on
  // hashes that parse as a Dest — #settings- and empty hashes are ignored.
  useEffect(() => {
    const applyDest = () => {
      const dest = destFromHash(window.location.hash);
      if (!dest) return;
      setSettingsOpen(false);
      setShowingAI(false);
      setExtraView(null);
      if (dest.subsection) {
        /* Card 4WYL7EXoxlJo: Nocturne's old #us-nocturne deep link still has
           to resolve. The tab is gone (it is a game inside Games now), so the
           legacy subsection lands on the Games screen, where Nocturne opens. */
        const usSection = dest.subsection === 'nocturne' ? 'game' : dest.subsection;
        if (dest.section === 'us') setUsTab(usSection);
        if (dest.section === 'life') setLifeTab(dest.subsection);
      }
      setTab(dest.section);
    };
    window.addEventListener('popstate', applyDest);
    return () => window.removeEventListener('popstate', applyDest);
  }, []);
}

export function useSettingsHashClear(settingsOpen: boolean) {
  // Closing Settings clears its hash (every close path: Done, a tab pick,
  // opening the concierge, leaving) — otherwise a reload would reopen a
  // screen the person just left.
  useEffect(() => {
    if (settingsOpen) return;
    if (settingsTabFromHash(window.location.hash)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [settingsOpen]);
}

export function usePresence(session: Session) {
  /* Card J9hHjSZDFTCV — the header mark glows while BOTH of us are watching.
     Two independent gates: the worker must report PEAR_PRESENCE (`=== true`,
     so an old worker or a failed config fetch stays dark) AND the partner's
     own socket must be in the room's latest snapshot. A tab this device hides
     clears locally as well, so the glow never claims togetherness for a
     window nobody is looking at. */
  const [partnerOnline, setPartnerOnline] = useState(false);
  useEffect(() => {
    const onPresence = (ev: Event) => {
      const online = (ev as CustomEvent<{ online?: unknown }>).detail?.online;
      const partner = session.members.find(m => m.memberId !== session.memberId);
      setPartnerOnline(Array.isArray(online) && !!partner && online.includes(partner.memberId));
    };
    const onVisibility = () => {
      if (document.hidden) setPartnerOnline(false);
    };
    window.addEventListener('pear:presence', onPresence as EventListener);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pear:presence', onPresence as EventListener);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [session.members, session.memberId]);
  const together =
    session.authConfig?.presence === true && partnerOnline && !document.hidden;

  /* Presence, stated as a fact about the partner (Jonathan 2026-10-30, option A
     of card O88pwsO9FbmS): the header line used to be the two names joined with
     "&" — a label, not a signal. It now names the partner and says which state
     they are in, with a dot that carries the same fact pre-attentively. The
     "&" pairing still reads from the names the two of you set; what it lost in
     symmetry it gained in being readable at a glance, which the flat line was
     not. Falls back to the pairing prompt when there is nobody to name yet. */
  const partnerMember = session.members.find(m => m.memberId !== session.memberId);
  const partnerName = partnerMember?.displayName || 'Your partner';
  /* Presence is only ever asserted in the POSITIVE (Jonathan 2026-10-30, second
     email on O88pwsO9FbmS): "<name> is here" while their socket is in the room,
     and the pairing line ("You & Nina") whenever it is not — the same two names
     the header carried before the dot shipped. Absence is not a fact this app
     can prove (they may be on the other device, asleep, or on an old worker),
     so "is away" overstated it. With the presence flag off there is no claim to
     make and the pairing line stands too. `together` stays the stricter
     both-watching gate for the mark, because a hidden tab is not evidence about
     the partner's socket. */
  const presenceKnown = session.authConfig?.presence === true;
  const pairText = session.members.map(m => m.displayName).filter(Boolean).join(' & ');
  const presenceHere = presenceKnown && session.members.length === 2 && partnerOnline;
  const presenceText = session.members.length === 2
    ? (presenceHere ? `${partnerName} is here` : (pairText || 'Both online'))
    : 'Waiting for your partner';
  return { together, presenceHere, presenceText };
}

export function useHeaderDate() {
  /* Header date (Jonathan 2026-10-17). Short weekday + day + month, the
     device's own locale — no timezone logic, no server call. Held in state and
     refreshed at the next LOCAL midnight plus on foreground: a PWA left open
     overnight would otherwise keep yesterday's date until some unrelated
     render happened (pre-deploy review, 2026-10-17). No per-second timer. */
  const [todayLabel, setTodayLabel] = useState(() => headerDate());
  useEffect(() => {
    let timer: number | undefined;
    const arm = () => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 2);
      timer = window.setTimeout(() => {
        setTodayLabel(headerDate());
        arm();
      }, Math.max(1000, next.getTime() - now.getTime()));
    };
    arm();
    const onReturn = () => {
      if (!document.hidden) setTodayLabel(headerDate());
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);
  return todayLabel;
}
